import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';

const BreadcrumbWrapper = styled.nav`
  position: fixed;
  top: 90px;
  left: 0;
  right: 0;
  width: 100%;
  background: white;
  border-bottom: 1px solid #f0f0f0;
  padding: 1rem 1rem;
  font-size: 0.875rem;
  z-index: 49;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 640px) {
    font-size: 0.75rem;
    top: 69px;
    padding: 0.75rem 1rem;
  }
`;

const BreadcrumbList = styled.ol`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BreadcrumbItem = styled.li`
  display: flex;
  align-items: center;
  
  &:not(:last-child)::after {
    content: '/';
    margin-left: 0.5rem;
    color: #9ca3af;
  }
`;

const BreadcrumbLink = styled(Link)`
  color: rgb(171 61 99 / var(--tw-text-opacity, 1));
  text-decoration: none;
  transition: color 0.2s ease;
  padding: 0 0.25rem;
  
  &:hover {
    color: rgb(150, 40, 80);
    text-decoration: underline;
  }
`;

const BreadcrumbText = styled.span`
  color: #6b7280;
  padding: 0 0.25rem;
`;

// Helper function to format route segment into readable text
const formatSegment = (segment) => {
  if (!segment) return '';
  
  // Replace hyphens with spaces and capitalize each word
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to get breadcrumb label
const getBreadcrumbLabel = (segment, index, paths) => {
  const labelMap = {
    
    treats: 'Treats',
    cart: 'Shopping Cart',
    'pick-up': 'Pick Up',
    checkout: 'Checkout',
    'my-orders': 'My Orders',
    payment: 'Payment',
    aboutus: 'About Us',
    contactus: 'Contact Us',
    success: 'Order Success',
    login: 'Login',
    signup: 'Sign Up',
  };

  return labelMap[segment] || formatSegment(segment);
};

const Breadcrumb = () => {
  const location = useLocation();
  const paths = location.pathname
    .split('/')
    .filter(Boolean)
    .map(path => decodeURIComponent(path));
  // Don't show breadcrumb on home page and login/signup pages
  if (paths.length === 0 || paths[0] === 'login' || paths[0] === 'signup') {
    return null;
  }

  let breadcrumbPath = '';

  return (
    <BreadcrumbWrapper aria-label="breadcrumb">
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink to="/">Home</BreadcrumbLink>
        </BreadcrumbItem>

        {paths.map((segment, index) => {
          breadcrumbPath += `/${segment}`;
          const isLast = index === paths.length - 1;
          const label = getBreadcrumbLabel(segment, index, paths);

          // Skip category segment entirely since there's no dedicated page for it
          if (segment === 'category') {
            return null;
          }

          // For the last segment, show as text instead of link
          if (isLast) {
            return (
              <BreadcrumbItem key={index}>
                <BreadcrumbText>{label}</BreadcrumbText>
              </BreadcrumbItem>
            );
          }

          return (
            <BreadcrumbItem key={index}>
              <BreadcrumbLink to={breadcrumbPath}>{label}</BreadcrumbLink>
            </BreadcrumbItem>
          );
        })}
      </BreadcrumbList>
    </BreadcrumbWrapper>
  );
};

export default Breadcrumb;
